import React, { useRef, useEffect, forwardRef, useState } from "react";
import { BottomSheet as SpringBottomSheet } from "react-spring-bottom-sheet";
import { X } from "lucide-react";
import "react-spring-bottom-sheet/dist/style.css";

/**
 * BottomSheet component built on react-spring-bottom-sheet
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display inside the bottom sheet
 * @param {Function} props.onClose - Function to call when closing the bottom sheet
 * @param {string} props.title - Bottom sheet title (optional)
 * @param {React.ReactNode} props.headerContent - Custom header content (optional)
 * @param {React.ReactNode} props.footerContent - Custom footer content (optional)
 * @param {boolean} props.showCloseButton - Whether to show the close button (default: true)
 * @param {number} props.zIndex - Custom z-index for the bottom sheet (default: 100)
 * @param {boolean} props.open - Whether the bottom sheet is open
 * @param {Function} props.onDismiss - Function to call when the bottom sheet is dismissed (alias for onClose)
 * @param {boolean} props.snapPoints - Array or function for snap points (default: returns 0.6 of window height)
 * @param {boolean} props.expandOnContentDrag - Whether to expand on content drag (default: true)
 * @param {string} props.headerPadding - Custom padding for the header inner content (default: none; wrapper padding is set via CSS)
 * @param {string} props.bodyPadding - Custom padding for the body (default: "tw-px-6 tw-py-4")
 * @param {React.RefObject} props.customRef - Custom ref for the bottom sheet
 * @param {number} props.animationDelay - Delay in ms before calling onClose after animation (default: 500)
 * @returns {JSX.Element}
 */
const BottomSheet = forwardRef(
  (
    {
      children,
      onClose,
      title,
      headerContent,
      footerContent,
      showCloseButton = true,
      zIndex = 101,
      open = false,
      onDismiss,
      snapPoints = ({ maxHeight }) => maxHeight * 0.85,
      expandOnContentDrag = true,
      headerPadding = "",
      bodyPadding = "tw-px-6 tw-py-4",
      customRef,
      animationDelay = 500,
      ...otherProps
    },
    ref
  ) => {
    const internalRef = useRef(null);
    const bottomSheetRef = ref || customRef || internalRef;

    // State to track if we're in the process of closing
    const [isClosing, setIsClosing] = useState(false);
    const closeTimeoutRef = useRef(null);

    // Update our internal state based on the open prop
    useEffect(() => {
      // When opened externally, reset closing state
      if (open) {
        setIsClosing(false);

        // Clear any pending close timeouts
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
      }
    }, [open]);

    // Clean up timeouts on unmount
    useEffect(() => {
      return () => {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
      };
    }, []);

    // Handle dismiss with animation delay
    const handleDismiss = () => {
      // If we're already in the process of closing, don't do anything
      if (isClosing) return;

      // Mark as closing (this can be used for visual transition hints)
      setIsClosing(true);

      // Set up a timeout to actually call onClose after animation completes
      closeTimeoutRef.current = setTimeout(() => {
        if (onClose) onClose();
        if (onDismiss) onDismiss();
        closeTimeoutRef.current = null;
        setIsClosing(false);
      }, animationDelay);
    };

    // Custom styles to apply to the bottom sheet
    const customStyles = {
      "--rsbs-backdrop-bg": "rgba(0, 0, 0, 0.5)",
      "--rsbs-bg": "white",
      "--rsbs-handle-bg": "#DEE1E3",
      "--rsbs-max-w": "100%",
      "--rsbs-ml": "auto",
      "--rsbs-mr": "auto",
      "--rsbs-overlay-rounded": "16px",
      zIndex: zIndex,
    };

    const headerNode =
      title || headerContent || showCloseButton ? (
        <div
          className={`${headerPadding ? `${headerPadding} ` : ""}tw-flex tw-flex-shrink-0 tw-items-center tw-justify-between tw-text-left`}
        >
          {title || headerContent ? (
            <div className="tw-w-full tw-min-w-0">
              {title && (
                <h2 className="tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-xl tw-font-bold tw-text-transparent">
                  {title}
                </h2>
              )}
              {headerContent}
            </div>
          ) : (
            <div />
          )}

          {showCloseButton && (
            <button
              onClick={handleDismiss}
              type="button"
              className="tw-rounded-full tw-p-2 hover:tw-bg-gray-100"
            >
              <X className="tw-h-6 tw-w-6 tw-text-gray-500" />
            </button>
          )}
        </div>
      ) : null;

    const footerNode =
      footerContent && (
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between">{footerContent}</div>
      );

    return (
      <SpringBottomSheet
        open={!isClosing}
        onDismiss={handleDismiss}
        snapPoints={snapPoints}
        expandOnContentDrag={expandOnContentDrag}
        ref={bottomSheetRef}
        style={customStyles}
        header={headerNode}
        footer={footerNode}
        {...otherProps}
      >
        <div
          className={`${bodyPadding} tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent`}
        >
          {children}
        </div>
        <style>
          {`
            [data-rsbs-overlay], [data-rsbs-backdrop] {
              z-index: ${zIndex} !important;
            }
            /* Header: same padding as footer, left-aligned (library defaults to center + extra top for handle) */
            [data-rsbs-root] [data-rsbs-header] {
              padding: 12px 16px;
              padding-top: calc(12px + env(safe-area-inset-top));
              padding-bottom: 12px;
              text-align: left;
            }
            /* Slightly less top so header height matches footer; handle stays visible */
            [data-rsbs-root] [data-rsbs-header]:before {
              top: calc(4px + env(safe-area-inset-top));
            }
            [data-rsbs-root] [data-rsbs-footer] {
              padding: 12px 16px;
              padding-bottom: calc(12px + env(safe-area-inset-bottom));
            }
          `}
        </style>
      </SpringBottomSheet>
    );
  }
);

BottomSheet.displayName = "BottomSheet";

export default BottomSheet;
